import { useState, useEffect, useMemo } from 'react';
import IconifyIcon from '../../../components/IconifyIcon';
import ReactApexChart from 'react-apexcharts';
import { Card, CardBody, CardFooter, CardTitle, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import apiService from '../../../services/api.service';
import { useAuth } from '../../../context/AuthContext';

const StatsCard = ({ amount, change, icon, name, variant, link }) => {
  return (
    <Col md={6} className="mb-3">
      <Card className="overflow-hidden">
        <CardBody>
          <Row className="align-items-center">
            <Col xs={6}>
              <div className="avatar-lg bg-soft-primary rounded flex-centered" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconifyIcon icon={icon} style={{ fontSize: '32px' }} className="text-primary" />
              </div>
            </Col>
            <Col xs={6} className="text-end">
              <p className="text-muted mb-0 text-truncate">{name}</p>
              <h3 className="text-dark mt-1 mb-0" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{amount}</h3>
            </Col>
          </Row>
        </CardBody>
        <CardFooter className="py-2 bg-light bg-opacity-50">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className={`text-${variant} icons-center`}>
                {variant === 'danger' ? (
                  <IconifyIcon icon="bxs:down-arrow" className="fs-12" />
                ) : (
                  <IconifyIcon icon="bxs:up-arrow" className="fs-12" />
                )}
                &nbsp;{change}%
              </span>
              <span className="text-muted ms-1 fs-12">Ce mois</span>
            </div>
            <Link to={link || '#'} className="text-reset fw-semibold fs-12">
              Voir Plus
            </Link>
          </div>
        </CardFooter>
      </Card>
    </Col>
  );
};

const Stats = () => {
  const { user } = useAuth();
  const [selectedCity, setSelectedCity] = useState('all');
  const [monthlyOrders, setMonthlyOrders] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [monthlySales, setMonthlySales] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [loading, setLoading] = useState(true);
  const [cardsStats, setCardsStats] = useState(null);
  const [cardsLoading, setCardsLoading] = useState(true);

  // Fetch cards stats
  useEffect(() => {
    const fetchCardsStats = async () => {
      try {
        setCardsLoading(true);
        const response = await apiService.get('/dashboard/cards-stats');
        if (response.success) {
          setCardsStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching cards stats:', error);
      } finally {
        setCardsLoading(false);
      }
    };
    fetchCardsStats();
  }, []);

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true);
        const url = selectedCity === 'all' 
          ? '/dashboard/monthly-sales'
          : `/dashboard/monthly-sales?city=${selectedCity}`;
        const response = await apiService.get(url);
        if (response.success) {
          setMonthlyOrders(response.data.orders || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
          setMonthlySales(response.data.sales || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        }
      } catch (error) {
        console.error('Error fetching sales data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSalesData();
  }, [selectedCity]);

  // Format number with K for thousands
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  // Build stats cards based on API data and user role
  const statsCards = useMemo(() => {
    if (!cardsStats) return [];

    const cards = [
      {
        icon: 'solar:cart-5-bold-duotone',
        name: 'Total Commandes',
        amount: formatNumber(cardsStats.orders?.total || 0),
        variant: cardsStats.orders?.trend === 'up' ? 'success' : 'danger',
        change: cardsStats.orders?.percentage || 0,
        link: '/admin/orders/orders-list'
      },
      {
        icon: 'bx:user-circle',
        name: 'Total Clients',
        amount: formatNumber(cardsStats.clients?.total || 0),
        variant: cardsStats.clients?.trend === 'up' ? 'success' : 'danger',
        change: cardsStats.clients?.percentage || 0,
        link: '/admin/customer/customer-list'
      },
      {
        icon: 'bxs:box',
        name: 'Total Produits',
        amount: formatNumber(cardsStats.products?.total || 0),
        variant: cardsStats.products?.trend === 'up' ? 'success' : 'danger',
        change: cardsStats.products?.percentage || 0,
        link: '/admin/products/product-list'
      }
    ];

    // 4th card based on user role
    if (user?.role === 'admin') {
      cards.push({
        icon: 'bx:dollar-circle',
        name: 'Revenu Total',
        amount: formatNumber(cardsStats.revenue?.total || 0) + ' DH',
        variant: cardsStats.revenue?.trend === 'up' ? 'success' : 'danger',
        change: cardsStats.revenue?.percentage || 0,
        link: '/admin/reports/finance'
      });
    } else {
      cards.push({
        icon: 'bxs:building-house',
        name: 'Total Entrepôts',
        amount: cardsStats.warehouses?.total || 0,
        variant: 'success',
        change: 0,
        link: '/admin/warehouses/warehouse-list'
      });
    }

    return cards;
  }, [cardsStats, user]);

  const chartOptions = useMemo(() => ({
    series: [
      {
        name: 'Commandes',
        type: 'bar',
        data: monthlyOrders.map(v => Number(v) || 0)
      },
      {
        name: 'Ventes (DH)',
        type: 'area',
        data: monthlySales.map(v => Number(v) || 0)
      }
    ],
    chart: {
      height: 313,
      type: 'line',
      toolbar: {
        show: false
      }
    },
    stroke: {
      dashArray: [0, 0],
      width: [0, 2],
      curve: 'smooth'
    },
    fill: {
      opacity: [1, 1],
      type: ['solid', 'gradient'],
      gradient: {
        type: 'vertical',
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90]
      }
    },
    markers: {
      size: [0, 0],
      strokeWidth: 2,
      hover: {
        size: 4
      }
    },
    xaxis: {
      categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
      axisTicks: {
        show: false
      },
      axisBorder: {
        show: false
      }
    },
    yaxis: {
      min: 0,
      axisBorder: {
        show: false
      }
    },
    grid: {
      show: true,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: -2,
        bottom: 0,
        left: 10
      }
    },
    legend: {
      show: true,
      horizontalAlign: 'center',
      offsetX: 0,
      offsetY: 5,
      markers: {
        width: 9,
        height: 9,
        radius: 6
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0
      }
    },
    plotOptions: {
      bar: {
        columnWidth: '30%',
        barHeight: '70%',
        borderRadius: 3
      }
    },
    colors: ['#c5a05e', '#22c55e'],
    tooltip: {
      shared: true,
      y: [
        {
          formatter: function (y) {
            if (typeof y !== 'undefined') {
              return y + ' commandes';
            }
            return y;
          }
        },
        {
          formatter: function (y) {
            if (typeof y !== 'undefined') {
              return y.toLocaleString() + ' DH';
            }
            return y;
          }
        }
      ]
    }
  }), [monthlyOrders, monthlySales]);

  return (
    <>
      <Col xxl={5} className="mt-3 mb-4">
        <Row className="g-3">
          <Col xs={12}>
            <div className="alert alert-warning text-truncate mb-0" role="alert" style={{ backgroundColor: '#c5a05e', borderColor: '#c5a05e', color: '#fff' }}>
              🍯 Bienvenue chez Délice du Fès — Tradition familiale depuis 1870
            </div>
          </Col>
          {cardsLoading ? (
            <Col xs={12} className="text-center py-4">
              <span className="spinner-border spinner-border-sm" role="status"></span>
              <span className="ms-2">Chargement...</span>
            </Col>
          ) : (
            statsCards.map((item, idx) => (
              <StatsCard key={idx} {...item} />
            ))
          )}
        </Row>
      </Col>
      <Col xxl={7} className="mt-3 mb-4">
        <Card className="h-100">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CardTitle as={'h4'}>Évolution des Ventes</CardTitle>
              <div className="d-flex gap-2">
                <button 
                  type="button" 
                  className="btn btn-sm"
                  style={{ 
                    backgroundColor: selectedCity === 'all' ? '#212529' : '#f8f9fa',
                    color: selectedCity === 'all' ? '#fff' : '#000',
                    border: '1px solid #dee2e6'
                  }}
                  onClick={() => setSelectedCity('all')}
                >
                  <span style={{ color: selectedCity === 'all' ? '#fff' : '#000' }}>Tout</span>
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  style={{ 
                    backgroundColor: selectedCity === 'Casablanca' ? '#212529' : '#f8f9fa',
                    color: selectedCity === 'Casablanca' ? '#fff' : '#000',
                    border: '1px solid #dee2e6'
                  }}
                  onClick={() => setSelectedCity('Casablanca')}
                >
                  <span style={{ color: selectedCity === 'Casablanca' ? '#fff' : '#000' }}>Casablanca</span>
                </button>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  style={{ 
                    backgroundColor: selectedCity === 'Fès' ? '#212529' : '#f8f9fa',
                    color: selectedCity === 'Fès' ? '#fff' : '#000',
                    border: '1px solid #dee2e6'
                  }}
                  onClick={() => setSelectedCity('Fès')}
                >
                  <span style={{ color: selectedCity === 'Fès' ? '#fff' : '#000' }}>Fès</span>
                </button>
              </div>
            </div>
            <div dir="ltr">
              <div id="dash-performance-chart" className="apex-charts" />
              {loading ? (
                <div className="text-center py-5">
                  <span className="spinner-border spinner-border-sm" role="status"></span>
                  <span className="ms-2">Chargement...</span>
                </div>
              ) : (
                <ReactApexChart
                  options={chartOptions}
                  series={chartOptions.series}
                  height={280}
                  type="line"
                  className="apex-charts"
                />
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default Stats;
